// eSign via NSDL / (n)Code / CDAC.
// India's Aadhaar-based electronic signature flow under IT Act 2000 §3A and
// CCA guidelines. The OTP-based eSign service issues a single-use signing
// certificate and returns a PKCS#7 signature over the user-provided PDF.
//
// In production the integrating app provides a CSP (Certifying Service
// Provider) endpoint and a redirect URI. Here we ship the request/response
// shape, the will-PDF preparation pipeline, and a mock signer for v0.

import type { ConnectorAdapter, ConnectorMode, ConnectorResult, ConnectorStatus } from './_types.ts';
import { devModeEnabled, err, fetchWithBudget, liveDisabled, nowIso, ok, shortHash } from './_types.ts';

export interface EsignRequest {
  readonly pdfBytes: Uint8Array;
  readonly signerName: string;
  readonly signerAadhaarLast4: string;
  readonly purpose: 'will' | 'nominee-declaration' | 'asset-disclosure' | 'other';
  readonly reasonShort: string;
}

export interface EsignReceipt {
  readonly receiptId: string;
  readonly txnId: string;
  readonly providerCode: 'NSDL' | 'NCODE' | 'CDAC' | 'MOCK';
  readonly signedPdfBytes: Uint8Array;
  readonly signedAt: string;
  readonly certificateSerial: string;
  readonly signedHashSha256B64: string;
  readonly signerName: string;
  readonly signerAadhaarLast4: string;
  readonly mode: ConnectorMode;
}

export interface EsignConnectorStatus extends ConnectorStatus {
  readonly id: 'esign-nsdl';
  readonly lastTxnId: string | null;
}

export interface EsignConfig {
  readonly cspEndpoint: string;
  readonly aspId: string;
  readonly redirectUri: string;
}

interface EsignPersist {
  readonly txnId: string;
  readonly providerCode: string;
  readonly signedAt: string;
  readonly signerAadhaarLast4: string;
}

const STORE_KEY = 'empire/connectors/esign-nsdl';

export class EsignAdapter
  implements ConnectorAdapter<EsignConnectorStatus, EsignReceipt>
{
  readonly id = 'esign-nsdl';
  readonly displayName = 'eSign · NSDL';
  readonly displayNameHi = 'ई-साइन · एनएसडीएल';
  readonly dpdpCategory = 'identity-aadhaar-otp-signature';
  readonly mode: ConnectorMode;
  readonly config: EsignConfig | null;
  #lastReceipt: EsignReceipt | null = null;
  #pending: EsignRequest | null = null;

  constructor(mode: ConnectorMode = 'test', config: EsignConfig | null = null) {
    this.mode = mode;
    this.config = config;
  }

  async getStatus(): Promise<EsignConnectorStatus> {
    const persisted = readPersist();
    return {
      id: this.id,
      mode: this.mode,
      connected: persisted !== null,
      lastSyncAt: persisted?.signedAt ?? null,
      principalIdHash: persisted?.signerAadhaarLast4 ? await shortHash(persisted.signerAadhaarLast4) : null,
      lastTxnId: persisted?.txnId ?? null,
      notes: this.mode === 'live'
        ? `Live eSign via CSP at ${this.config?.cspEndpoint ?? '(unconfigured)'}.`
        : 'Test mode — mock signer adds a labelled signature page and an SHA-256 receipt.',
    };
  }

  async connect(): Promise<ConnectorResult<EsignConnectorStatus>> {
    if (this.mode === 'live' && !devModeEnabled()) return err(liveDisabled(this.id));
    return ok(await this.getStatus());
  }

  async disconnect(): Promise<void> {
    this.#lastReceipt = null;
    this.#pending = null;
    if (typeof localStorage !== 'undefined') localStorage.removeItem(STORE_KEY);
  }

  prepare(req: EsignRequest): void {
    this.#pending = req;
  }

  async importPayload(): Promise<ConnectorResult<EsignReceipt>> {
    if (!this.#pending) {
      return err({ code: 'not-configured', message: 'No PDF queued. Call prepare(req) first.', retriable: false });
    }
    if (this.mode === 'live') {
      if (!devModeEnabled()) return err(liveDisabled(this.id));
      if (!this.config) {
        return err({ code: 'not-configured', message: 'eSign CSP config missing.', retriable: false });
      }
      try {
        const body = await buildXmlRequest(this.#pending, this.config);
        const res = await fetchWithBudget(
          this.config.cspEndpoint,
          { method: 'POST', body, headers: { 'content-type': 'application/xml' } },
          { retries: 1, timeoutMs: 30_000 }
        );
        if (!res.ok) return err({ code: 'network', message: `CSP ${res.status}`, retriable: true });
        const signedBytes = new Uint8Array(await res.arrayBuffer());
        const receipt = await this.#receipt(this.#pending, signedBytes, 'NSDL');
        await this.#persist(receipt);
        this.#lastReceipt = receipt;
        return ok(receipt);
      } catch (e) {
        return err({ code: 'network', message: e instanceof Error ? e.message : 'csp-error', retriable: true });
      }
    }
    const mockSigned = await mockSignPdf(this.#pending);
    const receipt = await this.#receipt(this.#pending, mockSigned, 'MOCK');
    await this.#persist(receipt);
    this.#lastReceipt = receipt;
    return ok(receipt);
  }

  mockPayload(): EsignReceipt {
    return {
      receiptId: 'mock-receipt-0001',
      txnId: 'mock-txn-0001',
      providerCode: 'MOCK',
      signedPdfBytes: new Uint8Array(0),
      signedAt: nowIso(),
      certificateSerial: '00:11:22:33:44:55:66:77',
      signedHashSha256B64: '',
      signerName: '',
      signerAadhaarLast4: '0000',
      mode: this.mode,
    };
  }

  lastReceipt(): EsignReceipt | null {
    return this.#lastReceipt;
  }

  async #receipt(req: EsignRequest, signed: Uint8Array, providerCode: 'NSDL' | 'NCODE' | 'CDAC' | 'MOCK'): Promise<EsignReceipt> {
    const digest = await crypto.subtle.digest('SHA-256', signed as unknown as BufferSource);
    let s = '';
    for (const b of new Uint8Array(digest)) s += String.fromCharCode(b);
    return {
      receiptId: `r-${crypto.randomUUID()}`,
      txnId: `t-${crypto.randomUUID()}`,
      providerCode,
      signedPdfBytes: signed,
      signedAt: nowIso(),
      certificateSerial: providerCode === 'MOCK' ? '00:00:00:00:DE:MO' : '',
      signedHashSha256B64: btoa(s),
      signerName: req.signerName,
      signerAadhaarLast4: req.signerAadhaarLast4,
      mode: this.mode,
    };
  }

  async #persist(r: EsignReceipt): Promise<void> {
    if (typeof localStorage === 'undefined') return;
    const rec: EsignPersist = {
      txnId: r.txnId,
      providerCode: r.providerCode,
      signedAt: r.signedAt,
      signerAadhaarLast4: r.signerAadhaarLast4,
    };
    localStorage.setItem(STORE_KEY, JSON.stringify(rec));
  }
}

function readPersist(): EsignPersist | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(STORE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as EsignPersist;
  } catch {
    return null;
  }
}

// Mock signer — appends a "MOCK ESIGN STAMP" trailer comment to the PDF
// bytes. Browsers and PDF viewers ignore comments, so the result remains
// a valid PDF and the user gets a labelled draft until live mode is wired.
async function mockSignPdf(req: EsignRequest): Promise<Uint8Array> {
  const stamp = new TextEncoder().encode(
    `\n%%MOCK_ESIGN purpose=${req.purpose} signer=${req.signerName} last4=${req.signerAadhaarLast4} ts=${nowIso()}\n`
  );
  const out = new Uint8Array(req.pdfBytes.length + stamp.length);
  out.set(req.pdfBytes, 0);
  out.set(stamp, req.pdfBytes.length);
  return out;
}

async function buildXmlRequest(req: EsignRequest, config: EsignConfig): Promise<string> {
  const digestBuf = await crypto.subtle.digest('SHA-256', req.pdfBytes as unknown as BufferSource);
  let s = '';
  for (const b of new Uint8Array(digestBuf)) s += String.fromCharCode(b);
  const digestB64 = btoa(s);
  return `<?xml version="1.0" encoding="UTF-8"?>
<Esign aspId="${escapeXml(config.aspId)}" reason="${escapeXml(req.reasonShort)}" responseUrl="${escapeXml(config.redirectUri)}" ver="2.1">
  <Docs>
    <InputHash id="1" hashAlgorithm="SHA-256" docInfo="${escapeXml(req.purpose)}">${digestB64}</InputHash>
  </Docs>
</Esign>`;
}

function escapeXml(s: string): string {
  return s.replace(/[<>&"']/g, (c) =>
    c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '&' ? '&amp;' : c === '"' ? '&quot;' : '&apos;'
  );
}

export function makeEsignConnector(mode: ConnectorMode, config: EsignConfig | null = null): EsignAdapter {
  return new EsignAdapter(mode, config);
}
